import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ToolSidebar from "../utils/ToolSideBar";
import { API_URL, authFetch } from "../utils/auth";
import "../../css/staff/InternalWorkspace.css";
import "../../css/staff/KnowledgeDocument.css";

const HTTP_METHODS = ["GET", "POST", "PATCH", "PUT", "DELETE"];

function formatDate(value) {
  if (!value) return "Not updated";
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function cleanMarkdownLabel(text = "") {
  return text
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/\[([^\]]+)\]\([^\s)]+\)/g, "$1")
    .trim();
}

function slugifyHeading(text, counts) {
  const base = cleanMarkdownLabel(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "section";
  counts[base] = (counts[base] || 0) + 1;
  return counts[base] === 1 ? base : `${base}-${counts[base]}`;
}

function parseEndpointHeading(text = "") {
  const clean = cleanMarkdownLabel(text);
  const match = clean.match(/^(GET|POST|PATCH|PUT|DELETE)\s+(.+)$/i);
  if (!match) return null;
  const method = match[1].toUpperCase();
  if (!HTTP_METHODS.includes(method)) return null;
  return { method, path: match[2].trim() };
}

function InlineText({ text }) {
  const parts = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^\s)]+\))/g;
  let lastIndex = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const token = match[0];
    if (token.startsWith("`")) {
      parts.push(<code key={parts.length}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("**")) {
      parts.push(<strong key={parts.length}>{token.slice(2, -2)}</strong>);
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^\s)]+)\)$/);
      parts.push(link ? <a key={parts.length} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a> : token);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

function isTableSeparator(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function splitTableRow(line) {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}

function tocDisplayLabel(label) {
  return label === "Endpoint Details" ? "Endpoint Reference" : label;
}

function KnowledgeTableOfContents({ items }) {
  const [activeId, setActiveId] = useState(items[0]?.id || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!items.length) return undefined;
    setActiveId((current) => current || items[0].id);
    const headings = items.map((item) => document.getElementById(item.id)).filter(Boolean);
    if (!headings.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: [0, 1] }
    );
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [items]);

  function handleClick(event, id) {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
    setOpen(false);
    window.history.replaceState(null, "", `#${id}`);
  }

  if (items.length <= 1) return null;

  const renderLinks = () => items.map((item) => (
    <a
      key={item.id}
      href={`#${item.id}`}
      className={`kb-toc-link level-${item.level} ${activeId === item.id ? "active" : ""}`}
      onClick={(event) => handleClick(event, item.id)}
    >
      {tocDisplayLabel(item.label)}
    </a>
  ));

  return (
    <>
      <details className="kb-toc-mobile" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
        <summary>On this page</summary>
        <nav aria-label="On this page mobile">{renderLinks()}</nav>
      </details>
      <aside className="kb-toc" aria-label="On this page">
        <strong>On this page</strong>
        <nav>{renderLinks()}</nav>
      </aside>
    </>
  );
}

function KnowledgeConfigRequestAction({ action, document }) {
  const [message, setMessage] = useState("");
  const label = action.config_type_label || action.config_type || "Configuration";
  const futurePayload = {
    configType: action.config_type,
    documentId: document?.id || null,
    documentSlug: document?.slug || "",
    sectionAnchor: action.section_anchor || "",
    projectId: document?.project?.id || null,
    notes: "",
  };

  function handleRequestClick() {
    // TODO: Wire Knowledge Base configuration requests into the SCD Ops configuration-request/approval workflow.
    setMessage(`${label} requests are not wired yet. This will route to SCD Ops in a future update.`);
    return futurePayload;
  }

  return (
    <aside className="kb-config-action" aria-label={`${label} configuration request`}>
      <div>
        <span className="kb-config-eyebrow">Project configuration</span>
        <h4>{action.title || `${label} Configuration`}</h4>
        {action.description && <p>{action.description}</p>}
      </div>
      <div className="kb-config-footer">
        <button type="button" onClick={handleRequestClick}>Request Config</button>
        {message && <span>{message}</span>}
      </div>
    </aside>
  );
}

function KnowledgeMarkdown({ content = "", document }) {
  const documentTitle = document?.title || "";
  const { nodes, toc } = useMemo(() => {
    const configActions = document?.config_actions || [];
    const lines = content.split("\n");
    const output = [];
    const headings = [];
    const headingCounts = {};
    let list = [];
    let listType = null;
    let code = [];
    let inCode = false;
    let quote = [];
    let skippedTitle = false;
    const renderedActionIds = new Set();
    const actionsByAnchor = configActions.reduce((acc, action) => {
      const anchor = action.section_anchor || "";
      if (!anchor) return acc;
      if (!acc[anchor]) acc[anchor] = [];
      acc[anchor].push(action);
      return acc;
    }, {});

    function renderConfigActions(anchor) {
      const actions = actionsByAnchor[anchor] || [];
      actions.forEach((action) => renderedActionIds.add(action.id));
      return actions.map((action) => <KnowledgeConfigRequestAction key={`config-${action.id}`} action={action} document={document} />);
    }

    function pushNode(node, anchor = "") {
      output.push(node);
      if (anchor) output.push(...renderConfigActions(anchor));
    }

    function flushList() {
      if (!list.length) return;
      const ListTag = listType === "ol" ? "ol" : "ul";
      pushNode(<ListTag key={`list-${output.length}`}>{list.map((item, index) => <li key={index}><InlineText text={item} /></li>)}</ListTag>);
      list = [];
      listType = null;
    }

    function flushCode() {
      if (!code.length) return;
      pushNode(<pre key={`code-${output.length}`}><code>{code.join("\n")}</code></pre>);
      code = [];
    }

    function flushQuote() {
      if (!quote.length) return;
      pushNode(<blockquote key={`quote-${output.length}`}>{quote.map((line, index) => <p key={index}><InlineText text={line} /></p>)}</blockquote>);
      quote = [];
    }

    function flushBlocks() {
      flushList();
      flushQuote();
    }

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      const trimmed = line.trim();

      if (trimmed.startsWith("```")) {
        if (inCode) {
          inCode = false;
          flushCode();
        } else {
          flushBlocks();
          inCode = true;
        }
        continue;
      }

      if (inCode) {
        code.push(line);
        continue;
      }

      if (!trimmed) {
        flushBlocks();
        continue;
      }

      if (trimmed.startsWith("|") && lines[index + 1] && isTableSeparator(lines[index + 1])) {
        flushBlocks();
        const header = splitTableRow(trimmed);
        index += 2;
        const rows = [];
        while (index < lines.length && lines[index].trim().startsWith("|")) {
          rows.push(splitTableRow(lines[index]));
          index += 1;
        }
        index -= 1;
        pushNode(
          <div className="kb-table-wrap" key={`table-${output.length}`}>
            <table>
              <thead><tr>{header.map((cell, cellIndex) => <th key={cellIndex}><InlineText text={cell} /></th>)}</tr></thead>
              <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{header.map((_, cellIndex) => <td key={cellIndex}><InlineText text={row[cellIndex] || ""} /></td>)}</tr>)}</tbody>
            </table>
          </div>
        );
        continue;
      }

      const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
      if (heading) {
        flushBlocks();
        const level = heading[1].length;
        const title = heading[2].trim();
        const label = cleanMarkdownLabel(title);
        if (level === 1 && !skippedTitle && label === documentTitle) {
          skippedTitle = true;
          continue;
        }
        const id = slugifyHeading(title, headingCounts);
        const endpoint = parseEndpointHeading(title);
        if (endpoint) {
          pushNode(
            <div id={id} className="kb-endpoint-heading" key={`endpoint-${output.length}`}>
              <span className={`kb-method method-${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
              <code>{endpoint.path}</code>
            </div>,
            id
          );
          continue;
        }
        if (level >= 2 && level <= 3) headings.push({ id, label, level });
        const HeadingTag = level === 1 ? "h2" : `h${level}`;
        pushNode(<HeadingTag id={id} key={`heading-${output.length}`}><InlineText text={title} /></HeadingTag>, id);
        continue;
      }

      if (/^(-|\*)\s+/.test(trimmed)) {
        flushQuote();
        if (listType && listType !== "ul") flushList();
        listType = "ul";
        list.push(trimmed.replace(/^(-|\*)\s+/, ""));
        continue;
      }

      if (/^\d+\.\s+/.test(trimmed)) {
        flushQuote();
        if (listType && listType !== "ol") flushList();
        listType = "ol";
        list.push(trimmed.replace(/^\d+\.\s+/, ""));
        continue;
      }

      if (trimmed.startsWith(">")) {
        flushList();
        quote.push(trimmed.replace(/^>\s?/, ""));
        continue;
      }

      if (/^---+$/.test(trimmed)) {
        flushBlocks();
        pushNode(<hr key={`hr-${output.length}`} />);
        continue;
      }

      flushBlocks();
      pushNode(<p key={`p-${output.length}`}><InlineText text={line} /></p>);
    }

    flushBlocks();
    flushCode();
    const unmatchedActions = configActions.filter((action) => !renderedActionIds.has(action.id));
    if (unmatchedActions.length) {
      output.push(...unmatchedActions.map((action) => <KnowledgeConfigRequestAction key={`config-${action.id}`} action={action} document={document} />));
    }
    return { nodes: output, toc: headings };
  }, [content, document, documentTitle]);

  return (
    <div className="kb-reader-layout">
      <article className="kb-markdown">{nodes}</article>
      <KnowledgeTableOfContents items={toc} />
    </div>
  );
}

function DocumentCard({ document }) {
  return (
    <Link className="knowledge-card" to={`/knowledge/${document.slug}`}>
      <div className="knowledge-card-head">
        <span className="knowledge-badge">{document.category_label}</span>
        <span className="knowledge-scope">{document.scope_label}</span>
        {document.project && <span className="knowledge-project">{document.project.name}</span>}
      </div>
      <h2>{document.title}</h2>
      <p>{document.summary}</p>
      <div className="knowledge-card-foot">
        <span>Updated {formatDate(document.updated_at)}</span>
        {document.can_edit && <strong>Edit</strong>}
      </div>
      {document.tags?.length > 0 && <div className="knowledge-tags">{document.tags.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}</div>}
    </Link>
  );
}

function KnowledgeDocumentHeader({ document, editing, onToggleEdit }) {
  return (
    <header className="kb-document-header">
      <nav className="kb-breadcrumb" aria-label="Breadcrumb">
        <Link to="/knowledge">Knowledge Base</Link>
        <span>/</span>
        <span>{document.category_label}</span>
        <span>/</span>
        <strong>{document.title}</strong>
      </nav>
      <div className="kb-header-main">
        <div>
          <h1>{document.title}</h1>
          <p>{document.summary}</p>
          <div className="kb-document-meta">
            <span>{document.category_label}</span>
            <span className="scope">{document.scope_label}</span>
            {document.project && <span>{document.project.name}</span>}
            <span>Updated {formatDate(document.updated_at)}</span>
          </div>
        </div>
        {document.can_edit && <button type="button" className="knowledge-edit-button" onClick={onToggleEdit}>{editing ? "Close editor" : "Edit Document"}</button>}
      </div>
    </header>
  );
}

export default function KnowledgeBasePage() {
  const { identifier } = useParams();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [project, setProject] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ title: "", summary: "", content: "", tags: "" });

  const loadList = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (project) params.set("project", project);
    try {
      const response = await authFetch(`${API_URL}/knowledge/${params.toString() ? `?${params}` : ""}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to load documentation.");
      setDocuments(data.results || []);
      setCategories(data.categories || []);
      setProjects(data.projects || []);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load documentation.");
    } finally {
      setLoading(false);
    }
  }, [search, category, project]);

  const loadDocument = useCallback(async () => {
    if (!identifier) return;
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/knowledge/${identifier}/`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to load document.");
      setDocument(data);
      setForm({ title: data.title || "", summary: data.summary || "", content: data.content || "", tags: (data.tags || []).join(", ") });
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load document.");
    } finally {
      setLoading(false);
    }
  }, [identifier]);

  useEffect(() => {
    setNotice("");
    setEditing(false);
    if (identifier) loadDocument(); else loadList();
  }, [identifier, loadDocument, loadList]);

  async function saveDocument() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const response = await authFetch(`${API_URL}/knowledge/${identifier}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: form.title,
          summary: form.summary,
          content: form.content,
          tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to save document.");
      setDocument(data);
      setForm({ title: data.title || "", summary: data.summary || "", content: data.content || "", tags: (data.tags || []).join(", ") });
      setEditing(false);
      setNotice("Document saved.");
    } catch (err) {
      setError(err.message || "Unable to save document.");
    } finally {
      setSaving(false);
    }
  }

  if (identifier) {
    return (
      <div className="staff-shell">
        <ToolSidebar active="knowledge" />
        <main className="staff-page internal-page knowledge-page kb-document-page">
          {loading ? <section className="staff-card"><p>Loading document...</p></section> : error ? <section className="staff-card"><p className="staff-empty inline">{error}</p></section> : document && (
            <>
              <KnowledgeDocumentHeader document={document} editing={editing} onToggleEdit={() => setEditing(!editing)} />
              {notice && <section className="staff-card knowledge-notice">{notice}</section>}
              {editing ? (
                <section className="staff-card knowledge-editor">
                  <label>Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
                  <label>Summary<textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></label>
                  <label>Content<textarea className="knowledge-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></label>
                  <label>Tags<input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></label>
                  <div className="knowledge-editor-actions">
                    <button type="button" onClick={saveDocument} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                    <button type="button" className="secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                  </div>
                </section>
              ) : (
                <section className="staff-card knowledge-reader kb-document-reader">
                  <KnowledgeMarkdown content={document.content} document={document} />
                </section>
              )}
            </>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="staff-shell">
      <ToolSidebar active="knowledge" />
      <main className="staff-page internal-page knowledge-page">
        <header className="staff-header internal-hero">
          <span>Knowledge Base</span>
          <h1>Documentation</h1>
          <p>Guides, procedures, technical references, and internal resources.</p>
        </header>
        <section className="staff-card internal-toolbar knowledge-toolbar">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documentation..." />
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All</option>
            {categories.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">All projects</option>
            {projects.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <button type="button" onClick={loadList}>Filter</button>
        </section>
        <section className="knowledge-grid">
          {loading ? <p className="staff-empty inline">Loading documentation...</p> : error ? <p className="staff-empty inline">{error}</p> : documents.length === 0 ? <p className="staff-empty inline">No documentation matches those filters.</p> : documents.map((item) => <DocumentCard key={item.id} document={item} />)}
        </section>
      </main>
    </div>
  );
}
